import { useState } from 'react';
import { Form, Input, Button } from 'antd';
import {
  MailOutlined,
  ArrowLeftOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { sanitizeEmail } from '../../utils/sanitize';
import { notifyError } from '../../utils/notify';

// Stage 5N-6: tenant Forgot Password — request stage.
//
// Privacy: the backend always returns a generic success envelope, so
// we never branch on "account found" / "account not found". After
// submit we just show the same confirmation copy regardless. This
// component is intentionally simple — it submits, swaps in a confirm
// state, and offers a link back to /login.
function AuthBadge({ icon, className = '' }) {
  return (
    <span
      aria-hidden="true"
      className={`inline-flex items-center justify-center rounded-xl bg-brand-500 text-white shadow-[0_8px_20px_-6px_rgba(24,119,242,0.55)] text-2xl ${className}`}
    >
      {icon}
    </span>
  );
}

export default function ForgotPasswordPage() {
  const [submitting, setSubmitting] = useState(false);
  const [submittedTo, setSubmittedTo] = useState(null);

  async function onFinish(values) {
    setSubmitting(true);
    try {
      const email = sanitizeEmail(values.email);
      await endpoints.auth.forgotPassword({ email });
      // Generic success — we don't tell the user whether the email
      // resolved to an account; the success copy is identical either
      // way to prevent enumeration.
      setSubmittedTo(email);
    } catch (err) {
      // RATE_LIMITED is the only error the user can act on at this
      // step; other backend issues fall through to a generic toast.
      notifyError(err, { title: 'Could not send reset instructions' });
    } finally {
      setSubmitting(false);
    }
  }

  // ------------------------ confirmation state ------------------------
  if (submittedTo) {
    return (
      <div className="gi-onboarding-rise w-full bg-white rounded-2xl shadow-elevated border border-surface-200 p-6 sm:p-8">
        <AuthBadge className="h-11 w-11" icon={<MailOutlined />} />
        <div className="mt-5 mb-3">
          <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0 leading-tight tracking-tight">
            Check your inbox
          </h2>
          <p className="text-sm text-ink-500 m-0 mt-1.5">
            If an account exists for{' '}
            <span className="font-medium text-ink-700">{submittedTo}</span>, we
            have sent password reset instructions. The link expires shortly,
            so use it soon.
          </p>
        </div>
        <p className="text-xs text-ink-400 m-0 mt-3">
          Did not receive the email? Check your spam folder, or wait a few
          minutes before requesting a new link.
        </p>

        <div className="mt-6 pt-4 border-t border-surface-200 flex justify-center">
          <Link
            to="/login"
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-surface-200 bg-white text-sm font-semibold text-ink-700 hover:text-brand-600 hover:border-brand-200 hover:bg-brand-50/40 active:bg-brand-50 transition-all duration-150 shadow-sm hover:shadow"
          >
            <ArrowLeftOutlined className="text-ink-400 group-hover:text-brand-500 transition-colors group-hover:-translate-x-0.5 transform duration-150" />
            Back to login
          </Link>
        </div>
      </div>
    );
  }

  // ------------------------ request state -----------------------------
  return (
    <div className="w-full bg-white rounded-2xl shadow-elevated border border-surface-200 p-6 sm:p-8">
      <AuthBadge className="h-11 w-11" icon={<KeyOutlined />} />

      <div className="mt-5 mb-6">
        <h2 className="text-2xl sm:text-3xl font-bold text-ink-900 m-0 leading-tight tracking-tight">
          Forgot password?
        </h2>
        <p className="text-sm text-ink-500 m-0 mt-1.5">
          Password reset is available only for Master accounts. Enter your
          Master account email and we will send reset instructions if the
          account is eligible.
        </p>
        <p className="text-xs text-ink-400 m-0 mt-2">
          Sub-users should ask their account Master to reset their password
          from the Users page.
        </p>
      </div>

      <Form
        layout="vertical"
        onFinish={onFinish}
        disabled={submitting}
        size="large"
        className="onboarding-compact-form"
      >
        <Form.Item
          label={<span className="text-sm font-semibold text-ink-800">Master account email</span>}
          name="email"
          rules={[
            { required: true, message: 'email is required' },
            // Client-side hint only — the backend accepts any email
            // shape and resolves master-vs-sub-user authoritatively
            // from the users table. We still require @master.glass-i
            // here so a confused sub-user doesn't waste a request
            // bucket. Backend remains the source of truth.
            {
              pattern: /^.+@master\.glass-i$/,
              message: 'must be a @master.glass-i email'
            }
          ]}
        >
          <Input
            prefix={<MailOutlined className="text-ink-400" />}
            placeholder="ravi@master.glass-i"
            autoComplete="username"
          />
        </Form.Item>

        <Form.Item className="!mb-0 !mt-1">
          <Button
            type="primary"
            htmlType="submit"
            loading={submitting}
            block
            className="!h-12 !text-base !font-semibold !shadow-[0_8px_20px_-8px_rgba(24,119,242,0.55)] hover:!shadow-[0_10px_24px_-8px_rgba(24,119,242,0.65)] active:!shadow-[0_4px_12px_-4px_rgba(24,119,242,0.55)] transition-shadow"
          >
            Send reset instructions
          </Button>
        </Form.Item>
      </Form>

      <div className="mt-5 pt-4 border-t border-surface-200 text-center">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-semibold text-brand-500 hover:text-brand-600 transition-colors"
        >
          <ArrowLeftOutlined />
          Back to login
        </Link>
      </div>
    </div>
  );
}
